import PathToRegexp from 'path-to-regexp';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

function registerRoutes(routes, interaction, parentData) {
    routes.forEach(function (route) {
        var data = interaction.register(route.public, parentData);
        registerRoutes(route.children, interaction, data);
    });
}

var withLeadingSlash = function (path) {
    return path.charAt(0) === '/' ? path : '/' + path;
};
var stripLeadingSlash = function (path) {
    return path.charAt(0) === '/' ? path.slice(1) : path;
};
var withTrailingSlash = function (path) {
    return path.charAt(path.length - 1) === '/' ? path : path + '/';
};
var join = function (beginning, end) {
    return withTrailingSlash(beginning) + end;
};

function generatePathname(options) {
    var knownPaths = {};
    var cache = {};
    return {
        name: "pathname",
        register: function (route, parent) {
            var name = route.name, path = route.path;
            if (knownPaths[name] !== undefined) {
                console.warn('A route with the name "' +
                    name +
                    '" already exists. Each route should' +
                    "have a unique name. By registering a route with a name that already exists, " +
                    "you are overwriting the existing one. This may break your application.");
            }
            var base;
            if (parent && knownPaths[parent]) {
                base = knownPaths[parent];
            }
            knownPaths[name] = base ? join(base, path) : path;
            return name;
        },
        get: function (name, params) {
            if (knownPaths[name] == null) {
                console.error("Could not generate pathname for " + name + " because it is not registered.");
                return;
            }
            var compile = cache[name]
                ? cache[name]
                : (cache[name] = PathToRegexp.compile(knownPaths[name]));
            return withLeadingSlash(compile(params, options));
        },
        reset: function () {
            knownPaths = {};
            cache = {};
        }
    };
}

function createRedirect(redirectTo, interactions) {
    var name = redirectTo.name, params = redirectTo.params, rest = __rest(redirectTo, ["name", "params"]);
    var pathname = interactions.pathname(name, params);
    return __assign({ pathname: pathname }, rest);
}
function finishResponse(match, interactions, resolved) {
    var route = match.route, response = match.response;
    if (!route.response) {
        return response;
    }
    var responseModifiers = route.response({
        resolved: resolved,
        name: response.name,
        params: __assign({}, response.params),
        location: response.location,
        route: interactions
    });
    if (!responseModifiers) {
        return response;
    }
    var settableProperties = [
        "status",
        "error",
        "body",
        "data",
        "title",
        "redirectTo"
    ];
    // only merge the valid properties onto the response
    settableProperties.forEach(function (p) {
        if (responseModifiers.hasOwnProperty(p)) {
            if (p === "redirectTo") {
                // special case
                response[p] = createRedirect(responseModifiers[p], interactions);
            }
            else {
                response[p] = responseModifiers[p];
            }
        }
    });
    return response;
}

function parseParams(params, fns) {
    if (!fns) {
        return params;
    }
    var output = {};
    // For each param, attempt to parse it. However, if that
    // fails, fall back to the string value.
    for (var key in params) {
        var value = params[key];
        var fn = fns[key];
        if (fn) {
            try {
                value = fn(value);
            }
            catch (e) {
                console.error(e);
                value = params[key];
            }
        }
        output[key] = value;
    }
    return output;
}

function matchRoute(route, pathname) {
    var testPath = stripLeadingSlash(pathname);
    var regExpMatch = route.pathMatching.re.exec(testPath);
    if (!regExpMatch) {
        return [];
    }
    var matchedSegment = regExpMatch[0], parsed = regExpMatch.slice(1);
    var params = {};
    route.pathMatching.keys.forEach(function (key, index) {
        params[key.name] = parsed[index];
    });
    var matches = [{ route: route, params: params }];
    // if there are no children routes, immediately accept the match
    if (!route.children.length) {
        return matches;
    }
    // children only need to match against unmatched segments
    var remainingSegments = testPath.slice(matchedSegment.length);
    var childrenLength = route.children.length;
    for (var i = 0; i < childrenLength; i++) {
        var matched = matchRoute(route.children[i], remainingSegments);
        if (matched.length) {
            matches = matches.concat(matched);
            break;
        }
    }
    // return matches if a child route matches or this route matches exactly
    return matches.length > 1 ||
        (route.pathMatching.mustBeExact && remainingSegments.length === 0)
        ? matches
        : [];
}
function createMatch(routeMatches, location) {
    var partials = [];
    var params = {};
    var bestMatch = routeMatches.pop();
    // handle ancestor routes
    routeMatches.forEach(function (match) {
        partials.push(match.route.public.name);
        Object.assign(params, parseParams(match.params, match.route.paramParsers));
    });
    // handle best match
    Object.assign(params, parseParams(bestMatch.params, bestMatch.route.paramParsers));
    return {
        route: bestMatch.route,
        response: {
            location: location,
            key: location.key,
            name: bestMatch.route.public.name,
            params: params,
            partials: partials,
            status: 200,
            body: undefined,
            data: undefined,
            title: ""
        }
    };
}
function matchLocation(location, routes) {
    // determine which route(s) match, then use the exact match
    // as the matched route and the rest as partial routes
    var routeLength = routes.length;
    for (var i = 0; i < routeLength; i++) {
        var routeMatches = matchRoute(routes[i], location.pathname);
        if (routeMatches.length) {
            return createMatch(routeMatches, location);
        }
    }
    // no matching route
    return {
        route: undefined,
        response: undefined
    };
}

/*
 * This will call any initial/every match functions for the matching route
 */
function resolveRoute(match) {
    var response = match.response;
    var on = match.route.public.on;
    return Promise.all([
        on.initial && on.initial(),
        on.every &&
            on.every({
                name: response.name,
                params: __assign({}, response.params),
                location: response.location
            })
    ]).then(function (_a) {
        var initial = _a[0], every = _a[1];
        return ({ error: null, initial: initial, every: every });
    }, function (error) { return ({ error: error, initial: null, every: null }); });
}

function once(fn) {
    var promise;
    var hasRun = false;
    return function () {
        if (hasRun) {
            return promise;
        }
        promise = fn();
        hasRun = true;
        return promise;
    };
}

var createRoute = function (options) {
    var name = options.name, path = options.path, _a = options.pathOptions, pathOptions = _a === void 0 ? {} : _a, _b = options.children, descriptorChildren = _b === void 0 ? [] : _b, response = options.response, _c = options.on, on = _c === void 0 ? {} : _c, extra = options.extra, paramParsers = options.params;
    // end defaults to true, so end has to be hardcoded for it to be false
    // set this before setting pathOptions.end for children
    var mustBeExact = pathOptions.end == null || pathOptions.end;
    var children = [];
    // when we have child routes, we need to perform non-end matching and
    // create route objects for each child
    if (descriptorChildren.length) {
        pathOptions.end = false;
        children = descriptorChildren.map(createRoute);
    }
    // keys is populated by PathToRegexp
    var keys = [];
    var re = PathToRegexp(path, keys, pathOptions);
    return {
        public: {
            name: name,
            path: path,
            keys: keys.map(function (key) { return key.name; }),
            on: {
                initial: on.initial && once(on.initial),
                every: on.every
            },
            extra: extra
        },
        pathMatching: {
            re: re,
            keys: keys,
            mustBeExact: mustBeExact
        },
        response: response,
        children: children,
        paramParsers: paramParsers
    };
};

function hasAsyncOnFunction(route) {
    var on = route.public.on;
    return !!(on && (on.every || on.initial));
}
function hasAsyncRoute(routes) {
    return routes.some(function (route) {
        if (hasAsyncOnFunction(route)) {
            return true;
        }
        if (route.children.length) {
            return hasAsyncRoute(route.children);
        }
        return false;
    });
}

function createRouter(history, routeArray, options) {
    if (options === void 0) { options = {}; }
    var _a = options.route, userInteractions = _a === void 0 ? [] : _a, _b = options.sideEffects, sideEffects = _b === void 0 ? [] : _b, cache = options.cache, pathnameOptions = options.pathnameOptions, _c = options.emitRedirects, emitRedirects = _c === void 0 ? true : _c;
    var sync = true;
    var beforeSideEffects = [];
    var afterSideEffects = [];
    sideEffects.forEach(function (se) {
        if (se.after) {
            afterSideEffects.push(se.effect);
        }
        else {
            beforeSideEffects.push(se.effect);
        }
    });
    var routes = [];
    var registeredInteractions = {};
    // add the pathname interaction to the provided interactions
    var allInteractions = userInteractions.concat(generatePathname(pathnameOptions));
    function setupRoutesAndInteractions(routeArray) {
        routes = routeArray.map(createRoute);
        sync = !hasAsyncRoute(routes);
        for (var key in registeredInteractions) {
            delete registeredInteractions[key];
        }
        allInteractions.forEach(function (interaction) {
            interaction.reset();
            registeredInteractions[interaction.name] = interaction.get;
            registerRoutes(routes, interaction);
        });
    }
    var responseHandlers = [];
    var oneTimers = [];
    var mostRecent = {
        response: null,
        navigation: null
    };
    function respond(fn, options) {
        var _a = options || {}, _b = _a.observe, observe = _b === void 0 ? false : _b, _c = _a.initial, initial = _c === void 0 ? true : _c;
        if (observe) {
            var newLength_1 = responseHandlers.push(fn);
            if (mostRecent.response && initial) {
                fn.call(null, __assign({}, mostRecent, { router: router }));
            }
            return function () {
                responseHandlers[newLength_1 - 1] = null;
            };
        }
        else {
            if (mostRecent.response && initial) {
                fn.call(null, __assign({}, mostRecent, { router: router }));
            }
            else {
                oneTimers.push(fn);
            }
        }
    }
    function emit(response, navigation) {
        // store for current() and respond()
        mostRecent.response = response;
        mostRecent.navigation = navigation;
        var resp = { response: response, navigation: navigation, router: router };
        beforeSideEffects.forEach(function (fn) {
            fn(resp);
        });
        responseHandlers.forEach(function (fn) {
            if (fn != null) {
                fn(resp);
            }
        });
        // calling one time responseHandlers after regular responseHandlers
        // ensures that those are called prior to the one time fns
        while (oneTimers.length) {
            var fn = oneTimers.pop();
            if (fn) {
                fn(resp);
            }
        }
        afterSideEffects.forEach(function (fn) {
            fn(resp);
        });
    }
    var activeResponse;
    function navigationHandler(pendingNav) {
        if (activeResponse) {
            activeResponse.cancel(pendingNav.action);
            activeResponse.cancelled = true;
        }
        activeResponse = pendingNav;
        var navigation = {
            action: pendingNav.action,
            previous: mostRecent.response
        };
        if (cache) {
            var cachedResponse = cache.get(pendingNav.location);
            if (cachedResponse != null) {
                cacheAndEmit(cachedResponse, navigation);
                return;
            }
        }
        var match = matchLocation(pendingNav.location, routes);
        // if no routes match, do nothing
        if (!match.route) {
            console.error("The current location (" + pendingNav.location.pathname + ") has no matching route, " +
                'so a response could not be generated. A catch-all route ({ path: "(.*)" }) ' +
                "can be used to match locations with no other matching route.");
            pendingNav.finish();
            return;
        }
        if (sync) {
            pendingNav.finish();
            var response = finishResponse(match, registeredInteractions, null);
            cacheAndEmit(response, navigation);
        }
        else {
            resolveRoute(match).then(function (resolved) {
                if (pendingNav.cancelled) {
                    return;
                }
                pendingNav.finish();
                var response = finishResponse(match, registeredInteractions, resolved);
                cacheAndEmit(response, navigation);
            });
        }
    }
    function cacheAndEmit(response, navigation) {
        activeResponse = undefined;
        if (cache) {
            cache.set(response);
        }
        if (!response.redirectTo || emitRedirects) {
            emit(response, navigation);
        }
        if (response.redirectTo !== undefined) {
            history.replace(response.redirectTo);
        }
    }
    // now that everything is defined, actually do the setup
    setupRoutesAndInteractions(routeArray);
    history.respondWith(navigationHandler);
    var router = {
        route: registeredInteractions,
        history: history,
        respond: respond,
        replaceRoutes: setupRoutesAndInteractions,
        current: function () {
            return {
                response: mostRecent.response,
                navigation: mostRecent.navigation
            };
        }
    };
    return router;
}

export default createRouter;



// WEBPACK FOOTER //
// ./node_modules/@curi/core/dist/curi.es.js